import React, { useState } from 'react';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { setDoc, doc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import './Register.css';
import { normalizeString, formatCPF, formatPhone } from '../utils/customFunctions';

export default function Register() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    cpf: '',
    phoneNumber: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;
    
    // Apply CPF mask
    if (name === 'cpf') {
      formattedValue = formatCPF(value);
    }
    
    // Apply phone mask
    if (name === 'phoneNumber') {
      formattedValue = formatPhone(value);
    }
    
    setFormData(prev => ({ ...prev, [name]: formattedValue }));
    
    // Clear specific error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Check password strength
    if (name === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
  };

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    return strength;
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'E-mail é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Por favor, insira um endereço de e-mail válido';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Senha é obrigatória';
    } else if (formData.password.length < 8) {
      newErrors.password = 'A senha deve ter pelo menos 8 caracteres';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Por favor, confirme sua senha';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'As senhas não coincidem';
    }

    // Display name validation
    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Nome completo é obrigatório';
    } else if (formData.displayName.trim().length < 2) {
      newErrors.displayName = 'O nome deve ter pelo menos 2 caracteres';
    }

    // CPF validation
    if (!formData.cpf.trim()) {
      newErrors.cpf = 'Número do CPF é obrigatório';
    } else if (formData.cpf.replace(/\D/g, '').length !== 11) {
      newErrors.cpf = 'CPF deve conter 11 dígitos';
    }

    // Phone number validation
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Número de telefone é obrigatório';
    } else if (formData.phoneNumber.replace(/\D/g, '').length < 10) {
      newErrors.phoneNumber = 'Número de telefone deve ter pelo menos 10 dígitos';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      await updateProfile(userCredential.user, { displayName: formData.displayName });
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        uid: userCredential.user.uid,
        email: formData.email,
        display_name: formData.displayName,
        display_name_normalized: normalizeString(formData.displayName),
        document_number: formData.cpf.replace(/\D/g, ''), // Store only numbers
        phone_number: formData.phoneNumber.replace(/\D/g, ''), // Store only numbers
        role: 'operator',
        created_time: serverTimestamp(),
        updated_time: serverTimestamp(),
      });
      navigate('/profile');
    } catch (err) {
      setErrors({ submit: getErrorMessage(err.code) });
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return 'Já existe uma conta com este e-mail.';
      case 'auth/invalid-email':
        return 'Por favor, insira um endereço de e-mail válido.';
      case 'auth/operation-not-allowed':
        return 'A criação de conta está atualmente desabilitada.';
      case 'auth/weak-password':
        return 'A senha é muito fraca. Por favor, escolha uma senha mais forte.';
      default:
        return 'Falha no cadastro. Tente novamente.';
    }
  };

  const getPasswordStrengthText = () => {
    switch (passwordStrength) {
      case 0:
      case 1: return { text: 'Muito Fraca', color: '#e53e3e' };
      case 2: return { text: 'Fraca', color: '#dd6b20' };
      case 3: return { text: 'Regular', color: '#d69e2e' };
      case 4: return { text: 'Boa', color: '#38a169' };
      case 5: return { text: 'Forte', color: '#00a86b' };
      default: return { text: '', color: '' };
    }
  };

  return (
    <div className="register-container">
      <div className="register-card">
        <div className="register-header">
          <Logo src="/logo-rastro-mais.png" height="44px" />
          <h1>Criar Conta</h1>
          <p>Junte-se a nós e comece sua jornada</p>
        </div>

        <form onSubmit={handleRegister} className="register-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="displayName">Nome Completo</label>
              <div className="input-wrapper">
                <input
                  id="displayName"
                  name="displayName"
                  type="text"
                  placeholder="Digite seu nome completo"
                  value={formData.displayName}
                  onChange={handleChange}
                  className={errors.displayName ? 'error' : ''}
                />
                <span className="input-icon"><i className="fas fa-user"></i></span>
              </div>
              {errors.displayName && <span className="field-error">{errors.displayName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="cpf">Número do CPF</label>
              <div className="input-wrapper">
                <input
                  id="cpf"
                  name="cpf"
                  type="text"
                  placeholder="000.000.000-00"
                  value={formData.cpf}
                  onChange={handleChange}
                  className={errors.cpf ? 'error' : ''}
                  maxLength="14"
                />
                <span className="input-icon"><i className="fas fa-id-card"></i></span>
              </div>
              {errors.cpf && <span className="field-error">{errors.cpf}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="phoneNumber">Número de Telefone</label>
              <div className="input-wrapper">
                <input
                  id="phoneNumber"
                  name="phoneNumber"
                  type="tel"
                  placeholder="(11) 99999-9999"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  className={errors.phoneNumber ? 'error' : ''}
                  maxLength="15"
                />
                <span className="input-icon"><i className="fas fa-phone"></i></span>
              </div>
              {errors.phoneNumber && <span className="field-error">{errors.phoneNumber}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email">Endereço de E-mail</label>
              <div className="input-wrapper">
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Digite seu e-mail"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? 'error' : ''}
                />
                <span className="input-icon"><i className="fas fa-envelope"></i></span>
              </div>
              {errors.email && <span className="field-error">{errors.email}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Senha</label>
            <div className="input-wrapper">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="Crie uma senha"
                value={formData.password}
                onChange={handleChange}
                className={errors.password ? 'error' : ''}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
            {formData.password && (
              <div className="password-strength">
                <div className="strength-bar">
                  <div 
                    className="strength-fill" 
                    style={{ 
                      width: `${(passwordStrength / 5) * 100}%`,
                      backgroundColor: getPasswordStrengthText().color 
                    }}
                  ></div>
                </div>
                <span 
                  className="strength-text"
                  style={{ color: getPasswordStrengthText().color }}
                >
                  {getPasswordStrengthText().text}
                </span>
              </div>
            )}
            {errors.password && <span className="field-error">{errors.password}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirmar Senha</label>
            <div className="input-wrapper">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirme sua senha"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={errors.confirmPassword ? 'error' : ''}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
            {errors.confirmPassword && <span className="field-error">{errors.confirmPassword}</span>}
          </div>

          {errors.submit && (
            <div className="error-message">
              <span className="error-icon"><i className="fas fa-exclamation-triangle"></i></span>
              {errors.submit}
            </div>
          )}

          <button 
            type="submit" 
            className="register-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Criando Conta...
              </>
            ) : (
              'Criar Conta'
            )}
          </button>
        </form>

        <div className="register-footer">
          <div className="signin-prompt">
            Já tem uma conta? <Link to="/login">Faça login</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
